import React, { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { useSwipe, clamp } from './useSwipe'

export interface BottomSheetProps {
  label: string
  open: boolean
  onClose: () => void
  children: React.ReactNode
  sheetRef?: React.Ref<HTMLDivElement>
  /** Ref-Callback für die Wischgeste auf dem Griff (nach unten schließt). */
  grabRef?: (el: HTMLElement | null) => void
  /** Ref-Callback für die Wischgeste auf der Abdunklung. */
  scrimRef?: (el: HTMLElement | null) => void
  /**
   * Eigenständiges Sheet (z. B. Einsatzwahl im Header): wird per Portal in die .ui-shell gehängt und über `open`
   * gesteuert, unabhängig vom Sheet "Alle Bereiche" der Shell (data-sheet / --sp der Shell werden nicht benutzt).
   */
  standalone?: boolean
}

/** Schließen-Knopf (X, Touch-Ziel 44 px) oben rechts im Sheet. */
function SchliessenKnopf({ onClose }: { onClose: () => void }) {
  return (
    <button type="button" className="ui-sheet-x" onClick={onClose} aria-label="Schließen">
      <X aria-hidden="true" />
    </button>
  )
}

/** Solange ein Sheet offen ist: Seite nicht scrollen/aktualisieren lassen (Pull-to-Refresh, Gummiband). */
function useSheetLock(open: boolean) {
  useEffect(() => {
    if (!open) return
    const cls = 'ui-sheet-lock'
    document.documentElement.classList.add(cls)
    return () => {
      // nur entfernen, wenn kein weiteres Sheet offen ist
      setTimeout(() => { if (!document.querySelector('.ui-shell[data-sheet="true"], .ui-xsheet[data-open="true"]')) document.documentElement.classList.remove(cls) }, 0)
    }
  }, [open])
}

/**
 * Bottom-Sheet mit Griff, Schließen-Knopf (X) und Abdunklung. Fortschritt (0..1) liegt als CSS-Variable --sp am .ui-shell-Root
 * (Shell-Sheet) bzw. am .ui-xsheet (standalone). Schließen: X, Tipp auf die Abdunklung, Wischen nach unten am Griff/Kopf.
 */
export function BottomSheet({ label, open, onClose, children, sheetRef, grabRef, scrimRef, standalone }: BottomSheetProps) {
  const xRoot = useRef<HTMLDivElement>(null)
  const innerSheet = useRef<HTMLDivElement>(null)
  useSheetLock(open)

  // Wischgeste für das eigenständige Sheet (Shell-Sheet bekommt sie von der AppShell)
  const swipeOpts = {
    axis: 'y' as const,
    accept: (d: number) => d > 0 && open,
    start: () => { xRoot.current?.classList.add('is-sdragging'); xRoot.current?.style.setProperty('--sp', '1') },
    move: (d: number) => { const h = innerSheet.current?.offsetHeight || 400; xRoot.current?.style.setProperty('--sp', String(clamp(1 - d / h, 0, 1))) },
    end: (d: number, v: number, c: boolean) => {
      const h = innerSheet.current?.offsetHeight || 400
      const bleibt = c || !(d / h > 0.25 || v > 0.5)
      xRoot.current?.classList.remove('is-sdragging')
      xRoot.current?.style.setProperty('--sp', bleibt ? '1' : '0')
      if (!bleibt) onClose()
    },
  }
  const xGrabSwipe = useSwipe(swipeOpts)
  const xScrimSwipe = useSwipe(swipeOpts)

  if (standalone) {
    const host = typeof document !== 'undefined' ? (document.querySelector('.ui-shell') ?? document.body) : null
    if (!host) return null
    return createPortal(
      <div className="ui-xsheet" ref={xRoot} data-open={open ? 'true' : 'false'} style={{ ['--sp' as string]: open ? 1 : 0 } as React.CSSProperties}>
        <div className="ui-sscrim" ref={xScrimSwipe as React.Ref<HTMLDivElement>} onClick={onClose} aria-hidden="true" />
        <div
          className="ui-sheet"
          role="dialog"
          aria-label={label}
          aria-modal={open}
          ref={el => { (innerSheet as React.MutableRefObject<HTMLDivElement | null>).current = el; if (typeof sheetRef === 'function') sheetRef(el); else if (sheetRef) (sheetRef as React.MutableRefObject<HTMLDivElement | null>).current = el }}
        >
          <div className="ui-sheet-top">
            <div className="ui-grab" ref={xGrabSwipe as React.Ref<HTMLDivElement>}><i /></div>
            <SchliessenKnopf onClose={onClose} />
          </div>
          <div className="ui-sheet-body scrollbar-thin">{children}</div>
        </div>
      </div>,
      host,
    )
  }
  return (
    <>
      <div className="ui-sscrim" ref={scrimRef as React.Ref<HTMLDivElement>} onClick={onClose} aria-hidden="true" />
      <div className="ui-sheet" role="dialog" aria-label={label} aria-modal={open} ref={sheetRef}>
        <div className="ui-sheet-top">
          <div className="ui-grab" ref={grabRef as React.Ref<HTMLDivElement>}><i /></div>
          <SchliessenKnopf onClose={onClose} />
        </div>
        <div className="ui-sheet-body scrollbar-thin">{children}</div>
      </div>
    </>
  )
}
